import i18n from '@app/dashboard/tools/i18n';
import { MAX_AMOUNT, MIN_AMOUNT } from '../../../utils';


export const BULK_SHEET_MAX_LENGTH = 300;
const BULK_SHEET_MIN_LENGTH = 1;
const BULK_SHEET_COLS_NUM = 7;
const {
  REF_ID,
  AMOUNT_CENTS,
  CLIENT_FIRST_NAME,
  CLIENT_LAST_NAME,
  EMAIL,
  PHONE_NUMBER,
  DESCRIPTION
} = {
  REF_ID: "reference_id",
  AMOUNT_CENTS: "amount_cents",
  CLIENT_FIRST_NAME: "client_first_name",
  CLIENT_LAST_NAME: "client_last_name",
  EMAIL: "email",
  PHONE_NUMBER: "phone_number",
  DESCRIPTION: "description"
};
const BULK_SHEET_COLS_NAMES = [
  REF_ID,
  AMOUNT_CENTS,
  CLIENT_FIRST_NAME,
  CLIENT_LAST_NAME,
  EMAIL,
  PHONE_NUMBER,
  DESCRIPTION
];

export const FIELDS_TO_DISPLAY_IN_DATATABLE = BULK_SHEET_COLS_NAMES;


const DESCRIPTION_REGEX = /^[\u0600-\u06FF\u0750-\u077F\uFB50-\uFBC1\uFBD3-\uFDFF\uFE70-\uFEFCa-zA-Z0-9\s.,'~!@#$%^&*()_\-+={}[]|\\<>?":;]+$/;
const FULL_NAME_REGEX = /^[\u0600-\u06FF\u0750-\u077F\uFB50-\uFBC1\uFBD3-\uFDFF\uFE70-\uFEFCa-zA-Z\s']+$/;
const EMAIL_REGEX = /^[^\W_](?!.*?-{2,})[a-zA-Z0-9_.-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z]{2,})+$/;
const BULK_PHONE_NUMBER_REGEX = /^\+?\d{8,14}$/;

type TValidationFnReturn = {
  isValid: boolean,
  error: string,
  modifiedData?: (string | number)[]
};

const getErrMsg = (err: string, col: number | string, index: number) => {
  const row = index + 1;
  return `${err}${i18n.t('innerLayout.paymentLinksCreationPages.bulkPaymentLinks.sections.customersSheet.errors.checkColXRowY', { col: col, row: row })}`;
};


export const checkAmount = ({
  colNumber,
  list,
  amountsFromSheet,
  fixedAmount
}: {
  colNumber: number,
  list: number[],
  amountsFromSheet: boolean,
  fixedAmount?: number
}): TValidationFnReturn => {

  let isValid = false;
  let error = '';
  let modifiedAmountsArr = list;

  isValid = list.every((amount, index) => {
    if (!amount) {
      if (amountsFromSheet || !fixedAmount) {
        error = getErrMsg(i18n.t('innerLayout.paymentLinksCreationPages.bulkPaymentLinks.sections.customersSheet.errors.amount.amountReq'), colNumber, index);
        return false;
      } else {
        modifiedAmountsArr[index] = fixedAmount;
        return true;
      }
    } else if (typeof amount !== 'number') {
      error = getErrMsg(i18n.t('innerLayout.paymentLinksCreationPages.bulkPaymentLinks.sections.customersSheet.errors.amount.invalidAmount'), colNumber, index);
      return false;
    } else if (amount > MAX_AMOUNT) {
      error = getErrMsg(i18n.t('innerLayout.paymentLinksCreationPages.bulkPaymentLinks.sections.customersSheet.errors.amount.maxAmount'), colNumber, index);
      return false;
    } else if (amount <= MIN_AMOUNT) {
      error = getErrMsg(i18n.t('innerLayout.paymentLinksCreationPages.bulkPaymentLinks.sections.customersSheet.errors.amount.invalidAmount'), colNumber, index);
      return false;
    } else {
      return true;
    }
  });

  return {
    isValid,
    error,
    modifiedData: modifiedAmountsArr
  }
};

export const checkRefId = ({
  colNumber,
  list
}: {
  colNumber: number,
  list: string[],
}): TValidationFnReturn => {
  const uniqueRefIds: string[] = [];
  let isValid = false;
  let error = '';

  isValid = list.every((refId, index) => {
    if (refId) {
      if (uniqueRefIds.includes(refId)) {
        error = getErrMsg(
          i18n.t('innerLayout.paymentLinksCreationPages.bulkPaymentLinks.sections.customersSheet.errors.refId.notUnique'), colNumber, index);
        return false;
      } else {
        uniqueRefIds.push(refId);
        return true;
      }
    }
    return true;
  });

  return {
    isValid,
    error
  }
};

export const checkClientName = ({
  colNumber,
  list,
  fieldName
}: {
  colNumber: number,
  list: string[],
  fieldName: string
}): TValidationFnReturn => {
  let isValid = false;
  let error = '';

  isValid = list.every((name, index) => {
    if (name && !FULL_NAME_REGEX.test(name)) {
      error = getErrMsg(i18n.t('innerLayout.paymentLinksCreationPages.bulkPaymentLinks.sections.customersSheet.errors.name.notValid', { field_name: fieldName }), colNumber, index);
      return false;
    } else {
      return true;
    }
  });

  return {
    isValid,
    error
  }
};

export const checkEmail = ({
  colNumber,
  list
}: {
  colNumber: number,
  list: string[],
}): TValidationFnReturn => {
  let isValid = false;
  let error = '';

  isValid = list.every((email, index) => {
    if (email && !EMAIL_REGEX.test(email)) {
      error = getErrMsg(i18n.t('innerLayout.paymentLinksCreationPages.bulkPaymentLinks.sections.customersSheet.errors.email.invalid'), colNumber, index);
      return false;
    } else {
      return true;
    }
  });

  return {
    isValid,
    error
  }
};

export const checkDescription = ({
  colNumber,
  list
}: {
  colNumber: number,
  list: string[],
}): TValidationFnReturn => {
  let isValid = false;
  let error = '';

  isValid = list.every((desc, index) => {
    if (desc && !DESCRIPTION_REGEX.test(desc)) {
      error = getErrMsg(i18n.t('innerLayout.paymentLinksCreationPages.bulkPaymentLinks.sections.customersSheet.errors.desc.invalid'), colNumber, index);
      return false;
    } else {
      return true;
    }
  });

  return {
    isValid,
    error
  }
};

export const checkPhone = ({
  colNumber,
  list
}: {
  colNumber: number,
  list: string[],
}): TValidationFnReturn => {
  let isValid = false;
  let error = '';

  isValid = list.every((phone, index) => {
    if (phone && !BULK_PHONE_NUMBER_REGEX.test(phone)) {
      error = getErrMsg(i18n.t('innerLayout.paymentLinksCreationPages.bulkPaymentLinks.sections.customersSheet.errors.phone.invalid'), colNumber, index);
      return false;
    } else {
      return true;
    }
  });

  return {
    isValid,
    error
  }
};


const validators = {
  [REF_ID]: checkRefId,
  [AMOUNT_CENTS]: checkAmount,
  [CLIENT_FIRST_NAME]: checkClientName,
  [CLIENT_LAST_NAME]: checkClientName,
  [EMAIL]: checkEmail,
  [PHONE_NUMBER]: checkPhone,
  [DESCRIPTION]: checkDescription
};

export const checkBulkSheetData = ({
  data,
  file,
  amountsFromSheet,
  fixedAmount
}: {
  data: any,
  file: File,
  amountsFromSheet: boolean,
  fixedAmount?: string
}
): Omit<TValidationFnReturn, 'modifiedData'> => {

  let isValid = false;
  let error = '';

  const labelsRow = Object.keys(data);

  const colsMaxLength = Math.max(...Object.values(data).map((arr: any) => arr?.length || 0));

  const isPhoneColEmpty = data?.[PHONE_NUMBER]?.every((phone: string) => !phone);
  const isEmailColEmpty = data?.[EMAIL]?.every((email: string) => !email);

  if (colsMaxLength < BULK_SHEET_MIN_LENGTH) {
    error = i18n.t('innerLayout.paymentLinksCreationPages.bulkPaymentLinks.sections.customersSheet.errors.entireSheet.empty');
  }
  else if (colsMaxLength > BULK_SHEET_MAX_LENGTH) {
    error = i18n.t('innerLayout.paymentLinksCreationPages.bulkPaymentLinks.sections.customersSheet.errors.entireSheet.largeData', { num: BULK_SHEET_MAX_LENGTH });
  }
  else if (labelsRow.length !== BULK_SHEET_COLS_NUM) {
    error = i18n.t('innerLayout.paymentLinksCreationPages.bulkPaymentLinks.sections.customersSheet.errors.entireSheet.rowsNumNotCorrect');
  }
  else if (BULK_SHEET_COLS_NAMES.join(',') !== labelsRow.join(',')) {
    error = i18n.t('innerLayout.paymentLinksCreationPages.bulkPaymentLinks.sections.customersSheet.errors.entireSheet.wrongOrder');
  }
  else if (isPhoneColEmpty && isEmailColEmpty) {
    error = i18n.t('innerLayout.paymentLinksCreationPages.bulkPaymentLinks.sections.customersSheet.errors.entireSheet.emailOrPhoneMustBeProvided');
  }
  else {
    // Check each row has either email or phone
    for (let i = 0; i < colsMaxLength; i++) {
      const email = data[EMAIL]?.[i];
      const phone = data[PHONE_NUMBER]?.[i];

      if (!email && !phone) {
        const emailColOrder = BULK_SHEET_COLS_NAMES.indexOf(EMAIL) + 1;
        const phoneColOrder = BULK_SHEET_COLS_NAMES.indexOf(PHONE_NUMBER) + 1;

        const colsNumbers = `${emailColOrder},${phoneColOrder}`;

        error = getErrMsg(i18n.t('innerLayout.paymentLinksCreationPages.bulkPaymentLinks.sections.customersSheet.errors.entireSheet.emailOrPhoneMustBeProvided'), colsNumbers, i);
        return { isValid: false, error };
      }
    }

    isValid = labelsRow.every((label, index) => {
      const validator = validators[label];

      const validatorResults = validator?.({
        colNumber: index + 1,
        list: data[label],
        ...(
          label === AMOUNT_CENTS ?
            {
              amountsFromSheet,
              fixedAmount: fixedAmount ? Number(fixedAmount) : undefined
            }
            :
            {}
        ) as any,
        ...(
          label === CLIENT_FIRST_NAME || label === CLIENT_LAST_NAME ?
            {
              fieldName: label
            }
            :
            {}
        ),
      });

      error = error || validatorResults.error;
      return validatorResults.isValid === true;
    });
  };

  return {
    isValid,
    error
  };
};

export const formatBulkData = (data: Record<string, (string | number)[]>) => {
  const headers = Object.keys(data);

  const lengths = headers.map(key => data[key].length);
  const numOfRows = Math.max(...lengths);

  const rows = Array.from({ length: numOfRows }, (_, index) => {
    return headers.map(key => data[key][index]);
  });

  const result = [headers, ...rows];
  return result;
};